/**
 * Acting on the page that hosts the widget (spec `page-tools.md`, level
 * `full`): the same gestures the user would make, so the site's own handlers
 * and its framework see them.
 */

import { allowedOptions, openListOf, type PageRules } from './snapshot'

const nativeValueSetter = (element: HTMLInputElement | HTMLTextAreaElement) =>
    Object.getOwnPropertyDescriptor(
        element instanceof HTMLTextAreaElement
            ? HTMLTextAreaElement.prototype
            : HTMLInputElement.prototype,
        'value',
    )?.set

const dispatchEdit = (element: Element) => {
    element.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
    element.dispatchEvent(new Event('change', { bubbles: true }))
}

/**
 * Writes `value` the way typing would: through the native setter, then
 * `input` and `change`. Assigning `value` alone leaves React's and Vue's state
 * behind, and the site submits what its state holds, not what the field shows.
 */
export function fill(
    element: Element,
    value: unknown,
    rules: PageRules,
): string {
    // What the user cannot change, the agent does not change either: a
    // disabled control, or one inside a disabled fieldset.
    if (element.matches(':disabled')) {
        throw new Error('The field is disabled or read-only')
    }
    if (element instanceof HTMLSelectElement) {
        const wanted = String(value).trim().toLowerCase()
        const options = allowedOptions(element, rules)
        const option = options.find(
            (o) =>
                o.value.toLowerCase() === wanted ||
                o.textContent?.trim().toLowerCase() === wanted,
        )
        if (!option) {
            throw new Error(
                `No option "${String(value)}": the options are ${options.map((o) => o.textContent?.trim()).join(', ')}`,
            )
        }
        element.value = option.value
        dispatchEdit(element)
        return `Selected "${option.textContent?.trim()}"`
    }
    if (
        element instanceof HTMLInputElement &&
        (element.type === 'checkbox' || element.type === 'radio')
    ) {
        const wanted = value === true || value === 'true' || value === 'on'
        if (element.checked !== wanted) {
            element.click()
        }
        return element.checked ? 'Checked' : 'Unchecked'
    }
    if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement
    ) {
        if (element.readOnly) {
            throw new Error('The field is disabled or read-only')
        }
        // Into the field as the user gets there, with the pointer: what the
        // field before left open (a date picker's calendar) closes on a press
        // outside it, never on a focus that moves (seen on demoqa, spec
        // `page-tools-improvements.md`, M10). Not into a combobox whose list
        // shows: the user is in it already, and the press would close the
        // list (MUI toggles it) as the text comes in, which then finds it
        // still open and does not open it again (seen on mui.com, spec
        // `page-point-and-choose.md`, C). A list on screen, not the attribute
        // alone, which a site may leave on
        if (!openListOf(element, rules)) {
            pointerPress(element)
            if (!element.isConnected) {
                throw new Error(
                    'The page replaced the field when the agent pressed it: read the page again',
                )
            }
        }
        element.focus()
        const setter = nativeValueSetter(element)
        if (setter) {
            setter.call(element, String(value))
        } else {
            element.value = String(value)
        }
        dispatchEdit(element)
        return `Wrote ${JSON.stringify(String(value))}`
    }
    if ((element as HTMLElement).isContentEditable) {
        const editable = element as HTMLElement
        const document = editable.ownerDocument
        editable.focus()
        // Typed as the user types: a rich editor keeps its own model of the
        // text and reads it from the input events, not from the DOM, so a
        // text set on the DOM is lost at its next change
        document.getSelection()?.selectAllChildren(editable)
        const typed =
            typeof document.execCommand === 'function' &&
            document.execCommand('insertText', false, String(value))
        if (!typed) {
            editable.textContent = String(value)
            dispatchEdit(element)
        }
        return `Wrote ${JSON.stringify(String(value))}`
    }
    throw new Error('This element does not take text: click it instead')
}

/** A mouse event at the center of `element`, where the agent's pointer is. */
const pointerAt = (element: Element) => {
    const { left, top, width, height } = element.getBoundingClientRect()
    return {
        bubbles: true,
        composed: true,
        cancelable: true,
        clientX: left + width / 2,
        clientY: top + height / 2,
    }
}

/** The element the agent's pointer last moved onto. */
let pointerIsOn: Element | undefined

/**
 * The pointer moving onto `element`, as a mouse gets there before it
 * presses: it enters once, so a hover and then a click on the same element
 * do not enter it twice, and moves every time.
 */
const pointerOnto = (element: Element) => {
    const init = pointerAt(element)
    const enter = { ...init, bubbles: false }
    const pointer = { pointerType: 'mouse', isPrimary: true }
    if (pointerIsOn !== element || !element.isConnected) {
        pointerIsOn = element
        element.dispatchEvent(
            new PointerEvent('pointerover', { ...init, ...pointer }),
        )
        element.dispatchEvent(
            new PointerEvent('pointerenter', { ...enter, ...pointer }),
        )
        element.dispatchEvent(new MouseEvent('mouseover', init))
        element.dispatchEvent(new MouseEvent('mouseenter', enter))
    }
    element.dispatchEvent(
        new PointerEvent('pointermove', { ...init, ...pointer }),
    )
    element.dispatchEvent(new MouseEvent('mousemove', init))
}

/**
 * A press of the pointer on `element`, down and up, without the click, at
 * its center, after the pointer moved there: a handler that tells a press
 * outside by the coordinates (a dialog closing on one) sees it inside, and a
 * list that opened on the press before takes the next one only after a move
 * (Radix's Select ignored a choice without it).
 */
const pointerPress = (element: Element) => {
    pointerOnto(element)
    // A page that swaps the element on hover: pressing the old one would
    // press nothing, and the result would say it did
    if (!element.isConnected) {
        throw new Error(
            'The page replaced the element when the pointer reached it: read the page again',
        )
    }
    const init = { ...pointerAt(element), button: 0 }
    const pointer = { ...init, pointerType: 'mouse', isPrimary: true }
    element.dispatchEvent(
        new PointerEvent('pointerdown', { ...pointer, buttons: 1 }),
    )
    element.dispatchEvent(new MouseEvent('mousedown', { ...init, buttons: 1 }))
    element.dispatchEvent(new PointerEvent('pointerup', pointer))
    element.dispatchEvent(new MouseEvent('mouseup', init))
}

/**
 * The pointer sequence, then `click()`: many custom controls listen to the
 * pointer and never to the click. It grants no user activation, so clipboard,
 * popups and fullscreen stay out of reach.
 */
export function click(element: Element): string {
    if ((element as HTMLButtonElement).disabled) {
        throw new Error('The element is disabled')
    }
    pointerPress(element)
    ;(element as HTMLElement).click()
    return 'Clicked'
}

/**
 * The pointer moving onto `element`: a menu or a tooltip that opens on hover
 * listens to these. A style that only `:hover` applies stays off, since no
 * real pointer is there.
 */
export function hover(element: Element): string {
    pointerOnto(element)
    return 'Hovered'
}

/** The keys `page_press` takes: the ones a page handles itself, never a browser shortcut. */
export const PAGE_KEYS = [
    'Enter',
    'Escape',
    'Tab',
    'Space',
    'ArrowDown',
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageDown',
    'PageUp',
    'Backspace',
    'Delete',
] as const

/**
 * `keydown` then `keyup` on `element`. True when the page handled the key
 * (it prevented the default): a key pressed by script has no default action,
 * so the caller does the one that matters when the page did not.
 */
export function press(element: Element, key: string): boolean {
    const init = {
        key: key === 'Space' ? ' ' : key,
        code: key,
        bubbles: true,
        composed: true,
        cancelable: true,
    }
    const handled = !element.dispatchEvent(new KeyboardEvent('keydown', init))
    element.dispatchEvent(new KeyboardEvent('keyup', init))
    return handled
}

/** The button Enter clicks in `form`, as the browser picks it: its first submit button. */
export const defaultButtonOf = (
    form: HTMLFormElement,
): HTMLElement | undefined =>
    [...form.elements].find(
        (control): control is HTMLElement => formSubmittedBy(control) === form,
    )

/**
 * The form Enter in `element` submits, as the browser decides it: a field
 * that takes a line of text, in a form whose first submit button is enabled,
 * or with that field alone and no button.
 */
export function formSubmittedByEnter(element: Element): HTMLFormElement | null {
    if (
        !(element instanceof HTMLInputElement) ||
        ['checkbox', 'radio', 'button', 'submit', 'reset', 'file'].includes(
            element.type,
        ) ||
        !element.form
    ) {
        return null
    }
    const { form } = element
    const button = defaultButtonOf(form)
    if (button) {
        return button.matches(':disabled') ? null : form
    }
    const lines = [...form.elements].filter(
        (control) =>
            control instanceof HTMLInputElement &&
            ![
                'checkbox',
                'radio',
                'button',
                'submit',
                'reset',
                'hidden',
            ].includes(control.type),
    )
    return lines.length === 1 ? form : null
}

/**
 * Enter's submission, as the browser does it: a click on the default button,
 * so the form is sent with it and its own handlers run, or the form alone
 * when it has no button.
 */
export function submitByEnter(form: HTMLFormElement): void {
    const button = defaultButtonOf(form)
    if (button) {
        button.click()
    } else {
        form.requestSubmit()
    }
}

/**
 * Stops the submits of `form` until released: a page's own handler of a key
 * may send the form (`requestSubmit()`, a click on its button) where the
 * user approved no card. Listened in capture at the form's root, before the
 * page's own listeners. A form sent with `submit()` or a request of the
 * page's own fires no event, and goes through as any script of the page.
 */
export function holdSubmits(form: HTMLFormElement): {
    readonly stopped: boolean
    release(): void
} {
    const root = form.getRootNode() as Document | ShadowRoot
    let stopped = false
    const listener = (event: Event) => {
        if (event.target === form) {
            event.preventDefault()
            event.stopImmediatePropagation()
            stopped = true
        }
    }
    root.addEventListener('submit', listener, true)
    return {
        get stopped() {
            return stopped
        },
        release: () => root.removeEventListener('submit', listener, true),
    }
}

/**
 * What Escape does in a dialog when the page leaves it to the browser: the
 * dialog's `cancel`, which the site may refuse, then its closing.
 */
export function cancelDialogOf(element: Element): boolean {
    const dialog = element.closest('dialog')
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) {
        return false
    }
    // Not in every browser yet: where missing, its two steps by hand
    const { requestClose } = dialog as { requestClose?: () => void }
    if (typeof requestClose === 'function') {
        requestClose.call(dialog)
    } else if (
        dialog.dispatchEvent(new Event('cancel', { cancelable: true }))
    ) {
        dialog.close()
    }
    return true
}

export function bringIntoView(element: Element) {
    element.scrollIntoView({ block: 'center', inline: 'nearest' })
}

/** The form a click on `element` would submit, if any. */
export function formSubmittedBy(element: Element): HTMLFormElement | null {
    if (
        (element instanceof HTMLButtonElement &&
            (element.type === 'submit' || !element.hasAttribute('type'))) ||
        (element instanceof HTMLInputElement &&
            (element.type === 'submit' || element.type === 'image'))
    ) {
        return element.form
    }
    return null
}

/** True for a link to another site: the user leaves the one they had open. */
export function leavesSite(element: Element): boolean {
    const link = element.closest('a[href]')
    if (!(link instanceof HTMLAnchorElement) || !leavesDocument(link)) {
        return false
    }
    try {
        return new URL(link.href, location.href).origin !== location.origin
    } catch {
        return false
    }
}

/**
 * True when a click on `element` would load another document: a link to
 * another path in the same tab. A link within the page (`#...`) or one a
 * client-side router intercepts still counts, because from here the two look
 * alike; the cost is one confirmation too many.
 */
export function leavesDocument(element: Element): boolean {
    const link = element.closest('a[href]')
    if (!(link instanceof HTMLAnchorElement)) {
        return false
    }
    if (link.target && link.target !== '_self') {
        return false
    }
    try {
        const target = new URL(link.href, location.href)
        return (
            target.origin !== location.origin ||
            target.pathname !== location.pathname ||
            target.search !== location.search
        )
    } catch {
        return false
    }
}
